import type { AuthError, Session, User } from '@supabase/supabase-js';
import { getSupabase } from '@/systems/SupabaseClient';

/**
 * AuthService — fachada fina sobre supabase.auth.
 *
 * Centraliza o formato dos dados que vão para o trigger SQL
 * `handle_new_user` (first_name/last_name/consent_at em
 * raw_user_meta_data) e devolve resultados normalizados em forma de
 * Result<T, string> pra UI tratar erros sem precisar conhecer o shape
 * do AuthError do Supabase.
 *
 * Nada aqui mexe em estado próprio — a sessão fica no Supabase (que
 * persiste em localStorage automaticamente). Esta camada é só conveniência.
 */

export interface SignUpInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  /** Consentimento LGPD/ECA (CLAUDE.md §15) — UI obriga marcar antes do submit. */
  consent: boolean;
}

export interface SignInInput {
  email: string;
  password: string;
}

export type AuthResult<T> = { ok: true; value: T } | { ok: false; error: string };

/** Cadastra um novo responsável; trigger SQL cria a row em `parents`. */
export async function signUp(input: SignUpInput): Promise<AuthResult<{ user: User | null; session: Session | null }>> {
  if (!input.consent) {
    return { ok: false, error: 'É necessário aceitar os termos para criar a conta.' };
  }
  const { data, error } = await getSupabase().auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      data: {
        first_name: input.firstName.trim(),
        last_name: input.lastName.trim(),
        consent_at: new Date().toISOString(),
      },
    },
  });
  if (error) return { ok: false, error: friendly(error) };
  return { ok: true, value: { user: data.user, session: data.session } };
}

export async function signIn(input: SignInInput): Promise<AuthResult<Session>> {
  const { data, error } = await getSupabase().auth.signInWithPassword({
    email: input.email.trim(),
    password: input.password,
  });
  if (error) return { ok: false, error: friendly(error) };
  if (!data.session) return { ok: false, error: 'Sessão não retornada — tente novamente.' };
  return { ok: true, value: data.session };
}

/** Manda e-mail de reset com link pro próprio domínio (Supabase preenche o token). */
export async function requestPasswordReset(email: string): Promise<AuthResult<true>> {
  const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined;
  const { error } = await getSupabase().auth.resetPasswordForEmail(email.trim(), { redirectTo });
  if (error) return { ok: false, error: friendly(error) };
  return { ok: true, value: true };
}

export async function signOut(): Promise<AuthResult<true>> {
  const { error } = await getSupabase().auth.signOut();
  if (error) return { ok: false, error: friendly(error) };
  return { ok: true, value: true };
}

export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await getSupabase().auth.getSession();
  return data.session;
}

/** Tradução PT-BR das mensagens mais comuns; cai em fallback genérico. */
function friendly(error: AuthError): string {
  const msg = error.message.toLowerCase();
  if (msg.includes('invalid login') || msg.includes('invalid_credentials'))
    return 'E-mail ou senha incorretos.';
  if (msg.includes('email not confirmed'))
    return 'Confirme o e-mail antes de entrar (verifique sua caixa de entrada).';
  if (msg.includes('user already registered') || msg.includes('already registered'))
    return 'Este e-mail já tem cadastro — tente entrar ou recuperar a senha.';
  if (msg.includes('password should be at least'))
    return 'A senha precisa ter pelo menos 6 caracteres.';
  if (msg.includes('rate limit') || msg.includes('too many'))
    return 'Muitas tentativas — espere um minuto e tente de novo.';
  return error.message || 'Erro inesperado. Tente novamente.';
}
